import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastAutoHideDuration } from 'Src/consts/duration';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { ListModal } from 'Src/feature/modal/ListModal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { UsedPayIn } from 'Src/feature/pay-in-out/usePayIn';
import { usePayInOut } from 'Src/feature/pay-in-out/usePayInOut';
import { EPXIndustry, EPXPayCompleteParams } from 'Src/feature/payment/paymentService';
import { useCurrentPayment } from 'Src/feature/payment/useCurrentPayment';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { ManualPaymentMessage, useManualPaymentWindow } from 'Src/feature/payment-manual/useManualPaymentWindow';
import { useOnErrorShowToast } from 'Src/feature/toasts/useOnErrorShowToast';
import { ToastAlertProps, useToast } from 'Src/feature/toasts/useToast';
import { sortByLabel } from 'Src/utils/sort';

const sectionId = 'PayInManual';

interface PayInManualProps {
  payInStore: UsedPayIn;
}

const PayInManual: FC<PayInManualProps> = ({ payInStore }) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast({ persist: true });

  const currentPayment = useCurrentPayment();
  const payInOuts = usePayInOut.query.getAll();
  const payInManual = usePayInOut.mutate.payInManual(useOnErrorShowToast({ title: t('payInOut.errors.payInManual') }));
  const ccPaymentMethod = usePaymentMethod.query.ccMethods();

  const modal = {
    payMethod: {
      ...useModalControl(),
      onSelection: (manualCCPaymentMethodId: number) => {
        void manualPaymentRequest({ manualCCPaymentMethodId });
      },
    },
  };

  const manualCCMethodsCNP = useMemo(() => ccPaymentMethod.data?.filter((d) => d.fields?.allowManualCardNotPresent === true) ?? [], [ccPaymentMethod.data]);
  const manualCCMethodOptions = useMemo(() => manualCCMethodsCNP.map((pm) => ({ value: pm.id as number, label: pm.name })).sort(sortByLabel), [manualCCMethodsCNP]);
  const canTakeManualPayment = manualCCMethodsCNP.length > 0;

  const { amount, notes, payInOutReasonId } = payInStore.form.state.watch();
  const disabled = !payInStore.form.state.isValid;
  const manualCCPaymentAmountRef = useRef(undefined as undefined | number);

  const windowCtrl = useManualPaymentWindow({
    id: sectionId,
    open: {
      url: '/pay.html',
      target: 'Payment Screen',
      features: 'popup',
    },
    onMessage: async (message: ManualPaymentMessage) => {
      payInStore.statusFailMessageSet(undefined);

      if (message.status === 'error') {
        log.error('onWindowMessage()', message);
      }

      if (message.type === 'epx' && message.status === 'pay-complete') {
        document.dispatchEvent(new CustomEvent('TerminalUiChangeTracker.autoLogOut.override', {
          detail: {
            isPause: false,
            reason: 'manual CC flow'
          }
        }));
        currentPayment.epx.status = message.data;
        windowCtrl.close();
        const {
          status,
          isPartialApproval,
          shouldVerifyId
        } = currentPayment.epx.status as EPXPayCompleteParams;

        // start fresh
        const nextAlertProps: ToastAlertProps = {
          title: undefined,
          message: undefined,
          messagePoints: [],
          severity: undefined,
          action: undefined,
        };

        if (status === 'approved') {
          nextAlertProps.severity = 'success';
          nextAlertProps.message = t('payment.labels.paymentApproved');

          if (isPartialApproval || shouldVerifyId) {
            if (isPartialApproval) {
              nextAlertProps.messagePoints?.push(t('payment.info.epxPartialApproval'));
            }
            if (shouldVerifyId) {
              nextAlertProps.messagePoints?.push(t('payment.info.epxShouldVerifyId'));
              // TODO: should we do anything other than a message when shouldVerifyId
            }
          }
        } else if (status === 'declined') {
          nextAlertProps.message = t('payment.labels.paymentDeclined');
          nextAlertProps.severity = 'warning';
          nextAlertProps.messagePoints = [
            `isPartialApproval: ${isPartialApproval ? 'true' : 'false'}`,
            `shouldVerifyId: ${shouldVerifyId ? 'true' : 'false'}`,
          ];
          payInStore.statusFailMessageSet(t('payment.labels.paymentDeclined'));
        }
        payInStore.modal.done.open();
        await payInOuts.refetch();

        toast.make({
          id: `${sectionId}.toast`,
          type: 'alert',
          autoHideDuration: ToastAutoHideDuration.Default,
          props: nextAlertProps,
        }).show();
      }
    },
  });

  const manualPaymentRequest = useCallback(async (options: { manualCCPaymentMethodId?: number, amount?: number }) => {

    // stash manual payment amt in case we have
    // a secondary flow (pick pay method, auth request) kick off
    if (!manualCCPaymentAmountRef.current) {
      manualCCPaymentAmountRef.current = options.amount;
    }

    // nothing we can do when payment method count
    if (manualCCMethodOptions.length < 1) return;

    if (manualCCMethodOptions.length > 1 && !options.manualCCPaymentMethodId) {
      modal.payMethod.open();
      return;
    }

    modal.payMethod.close();
    const amount = options.amount ? options.amount : manualCCPaymentAmountRef.current ?? 0;
    const payload = {
      manual: {
        paymentTypeId: options.manualCCPaymentMethodId ?? manualCCMethodsCNP[0]?.id as number, // Payment Type ID from bridge
        tip: 0, // can't have a tip on a Pay In transaction
        total: amount,
        // per product, manual payment is always to be considered "card not present" because there is no way to prove the card was present
        isCardPresent: false,
        isOpenTab: false,
      },
      payInOutTransaction: {
        amount,
        notes: notes ?? '',
        payInOutReasonId,
        paymentMethodId: options.manualCCPaymentMethodId ?? manualCCMethodsCNP[0]?.id as number, // Payment Type ID from bridge
      }
    };
    log.debug('manualPaymentRequest', { payload });
    // @ts-expect-error - checkId is a required field in the manual epx payload, but we cannot pass a checkId to the backend for a manual pay in, otherwise the API returns an error
    const resp = await payInManual.mutateAsync(payload);
    if (resp) {
      manualCCPaymentAmountRef.current = undefined;
      // per product, industry should always be 'M' because manual payments should always be considered "card not present"
      const respWithIndustry = Object.assign(resp, { industry: EPXIndustry.mobile });
      // store requested amounts in the case of a partial approval
      currentPayment.epx.response = respWithIndustry;
      log.debug('manualPaymentRequest', { respWithIndustry });
      const message: ManualPaymentMessage = { type: 'epx', status: 'pay', data: respWithIndustry };
      document.dispatchEvent(new CustomEvent('TerminalUiChangeTracker.autoLogOut.override', { detail: { isPause: true, reason: 'manual CC flow' } }));
      windowCtrl.open(message);
    }
  }, [currentPayment.epx, log, manualCCMethodOptions.length, manualCCMethodsCNP, modal.payMethod, notes, payInManual, payInOutReasonId, windowCtrl]);

  return (
    <>
      <ModalAction
        data-testid={sectionId}
        label={t('payInOut.labels.payManualEpxBtn')}
        disabled={disabled || !canTakeManualPayment}
        color="primary"
        onClick={() => void manualPaymentRequest({ amount })}
      />
      <ListModal
        data-testid={`${sectionId}-method-options`}
        color="warning"
        title={t('payment.action.selectCCMethod')}
        options={manualCCMethodOptions}
        onOptionClick={({ value }) => modal.payMethod.onSelection(value)}
        open={modal.payMethod.isOpen}
        onAfterOptionClick={() => {
          // do nothing after option click as "store.modal.payMethod.onSelection" will handle closing this modal
        }}
      />
    </>
  );
};

export type { PayInManualProps };
export { PayInManual, sectionId };
